// @ts-strict-ignore
import omit from 'lodash/omit';
import omitBy from 'lodash/omitBy';
import isUndefined from 'lodash/isUndefined';
import pick from 'lodash/pick';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment-timezone';

import {unique} from 'common/util/array';
import {TIMESTAMP_WITH_TIMEZONE_FORMAT} from 'common/constants';
import {
  ALL_MATH_TYPES,
  ResourceType,
  SHOW_LAST_X_DAYS_UNIT,
  SHOW_LAST_X_DAYS_VALUES,
  SPECIAL_EVENTS,
} from 'common/report/constants';
import {FiltersOperator, SelectorTypeDefinitions} from 'common/query-builder/query-entry-section/types';
import {
  ClientQueryTrackingProps,
  commonTrackingEventPropertyName,
  QueryTrackingPropName,
} from 'common/report/tracking-setup';
import {DateRange, DateRangeAttributes} from 'common/widgets/date-range-picker/models/date-range';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {Behavior, Segmentation} from 'common/types/reports/query';
import {CohortSegment, SegmentValue} from 'common/query-builder/query-block/segment-menu/types';
import {ComparisonDate} from 'common/query-builder/query-block/time-comparison-menu/types';
import {LiftComparison} from 'common/widgets/lift-comparison-menu/types';
import {_getDefaultInsightsSorting} from 'common/report/insights/util';
import {
  comparisonDateFromBookmarkFormat,
  comparisonDateToBookmarkFormat,
} from 'common/query-builder/query-block/time-comparison-menu/utils';
import {FilterSection} from 'common/report/insights/models/section';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {
  DisplayOptions,
  GroupClause as GroupClauseType,
  InsightsBookmarkSections,
  InsightsQueryError,
} from 'common/report/insights/util/types';
import {
  AnalysisType,
  BehaviorShowClause as BookmarkBehaviorShowClause,
  ChartPlotStyle,
  ChartType,
  ConversionWindowUnit,
  FunnelOrder,
  FunnelReentryModeType,
  FUNNELS_MATH_TYPES,
  InsightsBookmarkParams,
  InsightsResourceType,
  MetricType,
  PRIMITIVE_MATH_TYPES,
  RETENTION_MATH_TYPES,
  RetentionAlignmentType,
  RetentionType,
  RetentionUnboundedModeType,
  ValueRepresentationType,
} from 'common/types/reports/bookmark';
import {ShowSection, TimeSection} from 'common/report/insights/models/section';
import {InsightsReportSectionsOverride} from 'common/report/insights/models/types';
import {
  getBookmarkParamsWithAdditionalFilters,
  getBookmarkParamsWithChartType,
  getBookmarkParamsWithDateRange,
} from 'common/report/insights/util/bookmark-overrides';
import {LIFT_TRACKING_PREFIX, LiftTypeTrackingName, TIME_COMPARE_NON_LIFT_KEY} from 'common/report/util/lift/types';
import {DisplayBookmark} from 'common/report/dashboards/types';
import {getLiftTypeOverridesForInsightsBookmark} from 'common/report/insights/util/lift';
import {canUseViewN} from 'common/components/data-table/utils';
import {isBehaviorSegmentation} from 'common/query-builder/query-entry-section/row-types/behavior/utils';
import {MultiAttribution} from 'common/widgets/multi-attribution-menu/types';
import type {MetricsResource} from 'common/report/model-resources/metrics';
import {getFormulaShowClauseTrackingProps} from 'common/query-builder/query-entry-section/row-types/formula-entry/utils';
import {customPropertiesStore} from 'common/report/stores/custom-properties';
import {getCustomPropertyReferences} from 'common/report/insights/models/utils';
import {
  getBorrowedProperty,
  isBorrowedPropertyReferenced,
  trackingDataForBorrowedProperty,
} from 'common/widgets/custom-property-modal/util';
import type {BehaviorsResource} from 'common/report/model-resources/behaviors';
import {LocalizationStore} from 'common/i18n/store';
import {dataGroupStore} from 'common/report/stores/data-groups';
import {getAllPropertyDefinitionsForGroupProfiles} from 'common/report/stores/property-definitions';
import {isScdPropertyName, renameSCDProp} from 'common/report/util/properties';

import {FILTER_DETERMINER_ALL, QueryMode} from '../util/constants';
import {DEFAULT_STEPS_TABLE_COLUMNS} from '../visualization/constants';
import {BuilderSections} from './builder-sections';
import {Legend} from './legend';
import {DATE_RANGE_TYPE, RANGE_TO_TIME_CLAUSE_PARAMS, RANGES, TimeClause} from './time-clause';
import {BehaviorShowClause, FormulaShowClause, ShowClause, ShowClauseClassType} from './show-clause';
import {FilterClause} from './filter-clause';
import {TIME_UNITS} from './constants';
import {GroupClause as GroupClause} from './group-clause';

const REPORT_VISUALIZATION_PARAMS = [
  `displayOptions`,
  `sections`,
  `legend`,
  `sorting`,
  `timeComparison`,
  `liftComparison`,
  `columnWidths`,
];

export const INSIGHTS_DEFAULT_EVENT = {
  type: ResourceType.Event,
  aggregationOperator: ALL_MATH_TYPES.TOTAL,
  aggregationOperatorPerUser: null,
  dataGroupId: null,
  property: null,
  event: {
    custom: false,
    label: ``,
    value: null,
  },
  filters: [],
  filtersOperator: FiltersOperator.And,
  readOnlyEvent: false,
};

export const INSIGHTS_DEFAULT_METRIC = {
  type: SelectorTypeDefinitions.Metrics,
  event: null,
  filters: [],
};

// TODO: EVENTUALLY KILL THIS MODEL CLASS; report operations will directly read/modify a bookmark params JSON instead of using this OOP model
// saveable / loadable report metadata
export class Report {
  declare displayOptions: DisplayOptions;
  declare sections: BuilderSections;
  declare legend: Legend;
  declare sorting: TODO;
  declare timeComparison: ComparisonDate;
  declare liftComparison: LiftComparison;
  declare columnWidths: TODO;

  declare title?: string;
  declare description?: string;
  declare querySamplingEnabled: boolean;

  constructor(attrs: Partial<Report> = {}) {
    Object.assign(this, pick(attrs, [...REPORT_VISUALIZATION_PARAMS, `title`, `description`, `querySamplingEnabled`]));

    /**
     * Manual assignment to get typechecking working in insights-query
     * @type {BuilderSections}
     */
    this.sections = this.sections || new BuilderSections();
  }

  clone(withAttrs = {}) {
    // pass down custom event sync function so clauses can get the latest version of each custom event
    return Report.deserialize({
      ...this.serialize(),
      ...withAttrs,
    });
  }

  static deserialize(data) {
    // handle time comparison in old form in URL which may have `fromDate` or `toDate`
    const _oldDateRangeToNewDateRange = (oldDateRange) => {
      const {fromDate: from, offset, toDate: to} = oldDateRange;
      if (offset) {
        return {offset};
      } else {
        return {
          ...(from ? {from} : {}),
          ...(to ? {to} : {}),
        };
      }
    };

    return new Report({
      ...data,
      // handle URL with time comparison in old format
      ...(data?.timeComparison?.dateRange
        ? {timeComparison: _oldDateRangeToNewDateRange(data.timeComparison.dateRange)}
        : {}),
      sections: BuilderSections.deserialize(data.sections),
      ...(data?.liftComparison?.type ? {liftComparison: data.liftComparison} : {}),
      legend: Legend.deserialize({
        ...data.legend,
        // index selections are deprecated, however need to handle old urls that use it.
        ...(data.legendIdxSelections ? {idxSelections: data.legendIdxSelections} : {}),
        ...(data.individualSelectedSegments ? {individualSelectedSegments: data.individualSelectedSegments} : {}),
        ...(data.filteredBreakdownSegments ? {filteredBreakdownSegments: data.filteredBreakdownSegments} : {}),
      }),
    });
  }

  static createDefaultReport({
    dataGroupId = null,
    dataset = null,
    queryMode = QueryMode.Events,
    querySamplingEnabled = false,
    showLastXDays = null,
    defaultToSession = null,
  }: {
    dataGroupId?: string;
    dataset?: string;
    queryMode?: QueryMode;
    querySamplingEnabled?: boolean;
    showLastXDays?: string | null;
    defaultToSession?: boolean;
  } = {}) {
    const showMapping: Record<QueryMode, BookmarkBehaviorShowClause> = {
      [QueryMode.Groups]: {
        behavior: {
          type: MetricType.People,
          dataset,
          resourceType: InsightsResourceType.People,
          name: `$all_people`,
          dataGroupId,
        },
        display: {},
        measurement: {
          math: PRIMITIVE_MATH_TYPES.TOTAL,
        },
      },
      [QueryMode.Events]: {
        behavior: {
          type: MetricType.Event,
          dataset,
          resourceType: InsightsResourceType.Events,
          name: null,
          dataGroupId,
        },
        display: {},
        measurement: {
          math: PRIMITIVE_MATH_TYPES.TOTAL,
        },
      },
      [QueryMode.Funnels]: {
        behavior: {
          type: MetricType.Funnel,
          dataset,
          resourceType: InsightsResourceType.Events,
          behaviors: [
            {
              type: MetricType.Event,
              name: null,
              filtersDeterminer: FILTER_DETERMINER_ALL,
              filters: [],
            },
            {
              type: MetricType.Event,
              name: null,
              filtersDeterminer: FILTER_DETERMINER_ALL,
              filters: [],
            },
          ],
          name: null,
          dataGroupId,
          funnelOrder: FunnelOrder.Loose,
          aggregateBy: [],
          conversionWindowDuration: defaultToSession ? 1 : 7,
          conversionWindowUnit: defaultToSession ? ConversionWindowUnit.Session : ConversionWindowUnit.Day,
          funnelReentryMode: FunnelReentryModeType.Basic,
        },
        display: {},
        measurement: {
          math: defaultToSession ? FUNNELS_MATH_TYPES.SESSION : FUNNELS_MATH_TYPES.CONVERSION_RATE_UNIQUE,
          stepIndex: null,
          actionMode: null,
          actionStep: null,
        },
        isExpanded: true,
      },
      [QueryMode.Retention]: {
        behavior: {
          type: MetricType.Retention,
          dataset,
          resourceType: InsightsResourceType.Events,
          behaviors: [
            {
              type: MetricType.Event,
              name: null,
              filtersDeterminer: FILTER_DETERMINER_ALL,
              filters: [],
            },
            {
              type: MetricType.Event,
              name: null,
              filtersDeterminer: FILTER_DETERMINER_ALL,
              filters: [],
            },
          ],
          name: null,
          dataGroupId,
          retentionType: `birth` as RetentionType,
          retentionAlignmentType: RetentionAlignmentType.Birth,
          retentionUnit: TimeUnit.Day,
          retentionUnboundedMode: `none` as RetentionUnboundedModeType,
        },
        display: {},
        measurement: {
          math: RETENTION_MATH_TYPES.RETENTION_RATE,
          retentionBucketIndex: 0,
        },
        isExpanded: true,
      },
    };
    const chartType =
      queryMode === QueryMode.Groups
        ? ChartType.Bar
        : queryMode === QueryMode.Funnels
          ? ChartType.FunnelSteps
          : queryMode === QueryMode.Retention
            ? ChartType.RetentionCurve
            : ChartType.Line;

    return Report.deserialize({
      displayOptions: {
        chartType,
        plotStyle: ChartPlotStyle.Standard,
        analysis: AnalysisType.Linear,
        value: ValueRepresentationType.Absolute,
        ...(chartType === ChartType.FunnelSteps ? {funnelStepsSelectedTableColumns: DEFAULT_STEPS_TABLE_COLUMNS} : {}),
        // NOTE: This is used to differentiate bookmarks after we removed the
        // default softMin: 0 for Insights.
        primaryYAxisOptions: {},
      },
      sections: new BuilderSections({
        globalDataGroupId: dataGroupId,
        show: ShowSection.createShowSection(showMapping[queryMode]),
        time: new TimeSection(
          new TimeClause({
            ...(dataset && {dataset}),
            ...(showLastXDays && SHOW_LAST_X_DAYS_VALUES[showLastXDays]
              ? {
                  dateRangeType: DATE_RANGE_TYPE.RELATIVE,
                  value: null,
                  window: {
                    value: SHOW_LAST_X_DAYS_VALUES[showLastXDays],
                    unit: TIME_UNITS.DAY,
                  },
                  unit: SHOW_LAST_X_DAYS_UNIT[showLastXDays] || TIME_UNITS.DAY,
                }
              : RANGE_TO_TIME_CLAUSE_PARAMS[RANGES.MONTH]),
          }),
        ),
      }),
      legend: new Legend(),
      sorting: _getDefaultInsightsSorting({numSegmentColumns: 1, canUseViewN: canUseViewN()}),
      columnWidths: {
        bar: {},
      },
      title: ``,
      description: ``,
      timeComparison: null,
      shared: null,
      querySamplingEnabled,
    });
  }

  serialize(): InsightsBookmarkParams {
    const {
      displayOptions,
      sections,
      legend,
      sorting,
      timeComparison,
      liftComparison,
      columnWidths,
      title,
      querySamplingEnabled,
      description,
    } = this;
    const serialized = {
      sorting,
      timeComparison,
      liftComparison,
      columnWidths,
      title,
      querySamplingEnabled,
      description,
      // need to deep clone all values in display options because we do modify displayOptions
      // in InsightQueryBuilder#updateReportFromQBSelection
      displayOptions: {
        ...displayOptions,
      },
      sections: sections.serialize(),
      legend: legend.serialize(),
    };

    return serialized;
  }

  isPeopleTimeSeries() {
    return this.sections.show.isPeopleOnlyQuery() && this.sections.group.isPeopleTimeSeries();
  }

  timeUnit() {
    let unit = null;
    try {
      unit = this.sections.time.clauses[0].unit;
      if (this.isPeopleTimeSeries()) {
        for (const groupClause of this.sections.group.clauses) {
          if (groupClause.resourceType === `people` && groupClause.unit) {
            unit = groupClause.unit;
          }
        }
      }
    } catch {
      // ignore
    }
    return unit || TimeUnit.Day;
  }

  getDisplayAnalysis(): string {
    return (this.displayOptions && this.displayOptions.analysis) || `linear`;
  }

  showTimeUnitAggregation(): boolean {
    if (
      [ChartType.Metric, ChartType.RetentionCurve].includes(this.displayOptions.chartType) &&
      !this.sections.show.getBehaviorClauses()?.[0]?.behavior?.disableCohortize
    ) {
      return true;
    }
    return (
      [ChartType.Column, ChartType.Line].includes(this.displayOptions.chartType) &&
      this.sections.time.clauses.length &&
      !this.isPeopleTimeSeries()
    );
  }

  // MP bookmarks
  static fromBookmark(bookmark: DisplayBookmark<InsightsBookmarkParams>, defaultParams = {}) {
    const bookmarkParams = typeof bookmark.params === `string` ? JSON.parse(bookmark.params) : bookmark.params;
    const {timeComparison: timeComparisonParam} = bookmarkParams;
    const timeComparison = timeComparisonParam ? comparisonDateFromBookmarkFormat(timeComparisonParam) : null;
    return Report.deserialize({
      ...defaultParams,
      ...omit(bookmarkParams, [`timeComparison`]),
      ...(timeComparison ? {timeComparison} : null),
      title: bookmark.name,
      description: bookmark.description ?? ``,
    });
  }

  static fromBookmarkData(data: InsightsBookmarkParams): Report {
    return new Report({
      ...data,
      timeComparison: data.timeComparison ? comparisonDateFromBookmarkFormat(data.timeComparison) : null,
      sections: BuilderSections.deserialize(data.sections as any),
      ...(data?.liftComparison?.type ? {liftComparison: data.liftComparison} : {}),
      legend: Legend.deserialize(data.legend),
    });
  }

  toBookmark(): DisplayBookmark<InsightsBookmarkParams> {
    return {
      name: this.title,
      description: this.description,
      type: BookmarkType.Insights,
      params: this.toBookmarkData(),
    };
  }

  toBookmarkData(): InsightsBookmarkParams {
    const legend = this.legend.toBookmarkData();

    const params = {
      sections: this.sections.toBookmarkData() as InsightsBookmarkSections,
      ...(this.timeComparison ? {timeComparison: comparisonDateToBookmarkFormat(this.timeComparison)} : {}),
      ...(this.liftComparison ? {liftComparison: this.liftComparison} : {}),
      ...(!isEmpty(legend) ? {legend} : {}),
      ...pick(this, [`columnWidths`, `displayOptions`, `sorting`]),
    };

    return params;
  }

  isCohortsOverTime() {
    return this.sections.show.hasCohortsOverTime();
  }

  hasBehavioralFilterOrSegmentation() {
    return (
      this.sections.filter.clauses.some((filter) => isBehaviorSegmentation(filter as unknown as SegmentValue)) ||
      this.sections.group.clauses.some((group) => isBehaviorSegmentation(group as unknown as SegmentValue))
    );
  }

  // live state URL
  toUrlData() {
    const {
      columnWidths,
      description,
      displayOptions,
      sorting,
      timeComparison,
      liftComparison,
      querySamplingEnabled,
      title,
    } = this;
    return omitBy(
      {
        columnWidths,
        description,
        displayOptions,
        sorting,
        timeComparison,
        liftComparison,
        querySamplingEnabled,
        title,
        sections: this.sections.toUrlData(),
        legend: this.legend.serialize(),
      },
      isUndefined,
    );
  }

  _listOfSectionValues(section) {
    const reportSection = this.sections[section];
    return (
      reportSection &&
      reportSection.clauses.map((clause) => {
        if (clause instanceof ShowClause) {
          const showClause = clause as ShowClauseClassType;
          if (showClause instanceof FormulaShowClause) {
            return showClause.name;
          } else {
            return showClause.behavior.name;
          }
        } else {
          return clause.value;
        }
      })
    );
  }

  _xauTrackingData() {
    const xauOperators = [];
    this.sections.show.getBehaviorClauses().forEach((clause) => {
      if (clause.isXauClause()) {
        xauOperators.push(clause.measurement.math);
      }
    });

    return {xauOperators};
  }

  toGroupAnalyticsTrackingData(_params: {metricsResource?: MetricsResource} = {}) {
    const {group, filter} = this.sections;
    return {
      [commonTrackingEventPropertyName.dataGroupId]: this.sections.getDataGroupId(),
      [commonTrackingEventPropertyName.segmentByGroupProperty]: group.clauses.some((clause) => clause.dataGroupId),
      [commonTrackingEventPropertyName.filterByGroupProperty]: filter.clauses.some((clause) => clause.dataGroupId),
      [commonTrackingEventPropertyName.reportName]: `insights`,
    };
  }

  toLookupTableTrackingData() {
    const isLookupTableProperty = (prop) =>
      !!prop.dataGroupId && (Array.isArray(prop.value) || Array.isArray(prop.name));
    const countNumberOfLookupTablePropertyClauses = (clauses) => clauses.filter(isLookupTableProperty).length;

    const {show, group, filter} = this.sections;
    const aggregationClauses = show.getBehaviorClauses().filter((clause) => !!clause.measurement.property);

    const numberOfLookupTablePropertyGlobalFilters = countNumberOfLookupTablePropertyClauses(filter.clauses);
    const numberOfLookupTablePropertyEventFilters = show
      .getBehaviorClauses()
      .reduce((acc, clause) => acc + countNumberOfLookupTablePropertyClauses(clause.behavior.filters || []), 0);
    const numberOfLookupTablePropertyFilters =
      numberOfLookupTablePropertyGlobalFilters + numberOfLookupTablePropertyEventFilters;
    const numberOfLookupTablePropertyGroupBys = countNumberOfLookupTablePropertyClauses(group.clauses);
    const numberOfLookupTablePropertyAggregation = aggregationClauses.filter((clause) =>
      isLookupTableProperty(clause.measurement.property),
    ).length;

    return {
      [commonTrackingEventPropertyName.numberOfLookupTablePropertyFilters]: numberOfLookupTablePropertyFilters,
      [commonTrackingEventPropertyName.numberOfLookupTablePropertyGroupBys]: numberOfLookupTablePropertyGroupBys,
      [commonTrackingEventPropertyName.numberOfLookupTablePropertyAggregation]: numberOfLookupTablePropertyAggregation,
      [commonTrackingEventPropertyName.usedLookupTableProperties]:
        numberOfLookupTablePropertyFilters +
          numberOfLookupTablePropertyGroupBys +
          numberOfLookupTablePropertyAggregation >
        0,
    };
  }

  // Recursively cleans up any nullish or falsy values in the clause
  cleanClause(obj) {
    const result = {};
    for (const key in obj) {
      if (!obj[key] || (Array.isArray(obj[key]) && obj[key].length === 0)) {
        continue;
      } else if (typeof obj[key] === `object`) {
        result[key] = this.cleanClause(obj[key]);
      } else {
        result[key] = obj[key];
      }
    }
    return result;
  }

  // TODO opslead
  // Extracts Tracking Properties from the Report
  toTrackingData({
    behaviorsResource,
    error,
    i18n,
    id,
    metricsResource,
  }: {
    behaviorsResource?: BehaviorsResource;
    error?: InsightsQueryError;
    i18n: LocalizationStore;
    id?: number;
    metricsResource?: MetricsResource;
  }): ClientQueryTrackingProps {
    const showBehaviorClauses = this.sections.show.getBehaviorClauses();
    const formulaShowClauses = this.sections.show.getFormulaClauses();
    // TODO remove this? it should be named showClauseValues. value is also null for e.g. custom events clauses
    const compareClauses = this._listOfSectionValues(`show`);
    const groupClauseValues = this._listOfSectionValues(`group`);
    const groupByCohortsClauses = this.sections.group.clauses.filter((clause) => clause.isGroupByCohorts());
    const behaviorGroupClauses = this.sections.group.clauses
      .map((clause) => this.cleanClause(Object.assign({}, clause.behavior)) ?? null)
      .filter(Boolean);
    const customPropertyGroupClauses = this.sections.group.clauses.filter((c) => c.isCustomProperty);
    const customPropertyFilterClauses = this.sections.filter.clauses.filter((c) => c.isCustomProperty);
    const timeClause = this.sections[`time`].clauses[0];
    const clauses = [].concat(...Object.values(this.sections.attrs).map((section) => section.clauses));
    const datasets = unique(clauses.map((clause) => clause.dataset).filter(Boolean));

    // tracks the name of properties both in global and inline filters
    const filterClauseValues = this._listOfSectionValues(`filter`);
    const behaviorFilterClauses = this.sections.filter.clauses
      .map((clause) => this.cleanClause(Object.assign({}, clause.behavior)) ?? null)
      .filter(Boolean);
    const inlineFilterClauseValues = showBehaviorClauses.flatMap((clause) =>
      (clause.behavior.filters || []).map((filter) => filter.value),
    );
    const behaviorInlineFilters = showBehaviorClauses.flatMap((clause) =>
      (clause.behavior.filters || [])
        .map((filter) => this.cleanClause(Object.assign({}, filter.behavior)) ?? null)
        .filter(Boolean),
    );

    const cohortList = groupByCohortsClauses.flatMap((clause) => clause.cohorts);
    const cohortInverseNegationSet = groupByCohortsClauses.reduce((acc, clause) => {
      clause.cohorts.forEach((cohort) => {
        acc[cohort.id] = acc[cohort.id] ? acc[cohort.id].add(cohort.negated) : new Set([cohort.negated]);
      });
      return acc;
    }, {});
    const numInverseCohortsPairs = Object.entries(cohortInverseNegationSet).filter(
      ([, negationTypes]: any) => negationTypes.size === 2,
    ).length;
    const aggregationClauses: BehaviorShowClause[] = showBehaviorClauses.filter(
      (clause) => !!clause.measurement.property,
    );

    const pqaFilters = this.sections.filter.clauses.filter(
      (clause) => clause.behavior?.behaviorType === `$behavior_aggregation`,
    );
    const pqaBreakdowns = this.sections.group.clauses.filter(
      (clause) => clause.behavior?.behaviorType === `$behavior_aggregation`,
    );

    const globalDataGroupId = this.sections.getDataGroupId();
    let globalDataGroupName = null;
    let globalDataGroupProperty = null;
    if (globalDataGroupId && globalDataGroupId in dataGroupStore.value) {
      ({displayName: globalDataGroupName, propertyName: globalDataGroupProperty} =
        dataGroupStore.value[this.sections.getDataGroupId()]);
    }

    const scdOptionsForFilters = this.sections.filter.clauses
      .filter((clause) => !!clause?.scdOptions)
      .map((clause) => clause.scdOptions);
    const scdOptionsForBreakdowns = this.sections.group.clauses
      .filter((clause) => !!clause?.scdOptions)
      .map((clause) => clause.scdOptions);
    const scdOptionsForShowBehaviors = showBehaviorClauses
      .flatMap((clause) => clause.behavior?.filters ?? [])
      .filter((clause) => !!clause?.scdOptions)
      .map((clause) => clause.scdOptions);

    const attributeByFilters = this.sections.filter.clauses
      .filter((clause) => clause.value === `$multi_attribution_filter`)
      .map((clause) => this.cleanClause(clause));

    const attributeByBehaviors: Array<Behavior | undefined> = this.sections.group.clauses.map((clause) => {
      const {behavior, customGroups} = clause as GroupClauseType;
      return behavior?.aggregationOperator === PRIMITIVE_MATH_TYPES.MULTI_ATTRIBUTION
        ? Object.assign({...behavior}, customGroups ? {customGroups} : {})
        : undefined;
    });
    const multiAttributionMetrics: Array<MultiAttribution | undefined> = this.sections.show.clauses.map((clause) => {
      return clause.measurement.multiAttribution;
    });

    const hiddenClauseTypes = this.sections.show.clauses.reduce((acc, clause) => {
      if (clause.isHidden) {
        const type = clause instanceof FormulaShowClause ? `formula` : clause.behavior.type;
        acc[type] = (acc[type] ?? 0) + 1;
      }
      return acc;
    }, {});

    const customPropertiesReferences = getCustomPropertyReferences(this.toBookmarkData())
      .map(({referenceLocation, customPropertyId}) => {
        const customProperty = customPropertiesStore.value[customPropertyId];
        if (!customProperty) {
          return null;
        }
        return {
          referenceLocation,
          customProperty,
        };
      })
      .filter(Boolean);

    const borrowedPropertiesTrackingData = customPropertiesReferences
      .map(({referenceLocation, customProperty}) => {
        const borrowedProperty = getBorrowedProperty(customProperty);
        if (!borrowedProperty) {
          return null;
        }

        return {
          ...trackingDataForBorrowedProperty(customProperty),
          referenceLocation,
        };
      })
      .filter(Boolean);

    const nestedBorrowedPropertiesTrackingData = customPropertiesReferences
      .filter(
        ({customProperty}) => isBorrowedPropertyReferenced(customProperty) && !getBorrowedProperty(customProperty),
      )
      .map(({customProperty}) => {
        return {
          customPropertyId: customProperty.customPropertyId,
          name: customProperty.name,
        };
      });

    let hasSubDayGranularityTrackingData = false;
    try {
      hasSubDayGranularityTrackingData = DateRange.hasSubDayGranularity({
        type: timeClause.dateRangeType,
        from: Array.isArray(timeClause.value) ? timeClause.value[0] : null,
        to: Array.isArray(timeClause.value) ? timeClause.value[1] : null,
        window: timeClause?.window,
        rolling: null,
      });
    } catch (err) {
      console.error(`Error checking timeClause for sub-day granularity.`, err);
    }

    const retentionStats = showBehaviorClauses
      .filter((showClause) => showClause.behavior?.type === `retention`)
      .map((showClause) => {
        const math =
          showClause.measurement.math === `total` && showClause.measurement.property
            ? `property_sum`
            : showClause.measurement.math;
        return {
          math,
          property: showClause.measurement.property?.name,
          retentionUnboundedMode: showClause.behavior.retentionUnboundedMode,
          math_and_mode: `${math}_${showClause.behavior.retentionUnboundedMode}`,
          cohortize: !showClause.behavior.disableCohortize,
          cohortizeSet: showClause.behavior.disableCohortize !== undefined,
        };
      });

    const funnelStats = showBehaviorClauses
      .filter((showClause) => showClause.behavior?.type === `funnel`)
      .map((showClause) => {
        return {
          funnel_reentry_mode: showClause.behavior.funnelReentryMode,
        };
      });

    const isUserQuery = !!showBehaviorClauses.find((c) => !c?.behavior?.dataGroupId);
    const groupClausesInBreakdowns = this.sections.group.clauses.filter(
      (clause) => clause?.dataGroupId && typeof clause?.propertyFilter?.propertyName === `string`,
    );
    const groupClausesInFilters = this.sections.filter.clauses.filter((clause) => clause?.dataGroupId);

    let numOfUserGroupPropsInBreakdowns = 0;
    let numOfUserGroupPropsInFilters = 0;
    const isUserGroupQuery = isUserQuery && (groupClausesInBreakdowns.length > 0 || groupClausesInFilters.length > 0);
    if (isUserGroupQuery) {
      const groupProperties = getAllPropertyDefinitionsForGroupProfiles();
      const scdGroupPropertiesWithoutPrefix = new Set<string>(
        groupProperties.filter((prop) => isScdPropertyName(prop.name)).map((prop) => renameSCDProp(prop.name)),
      );

      numOfUserGroupPropsInBreakdowns = groupClausesInBreakdowns.reduce(
        (count, clause) =>
          scdGroupPropertiesWithoutPrefix.has(clause?.propertyFilter?.propertyName as string) ? count + 1 : count,
        0,
      );

      numOfUserGroupPropsInFilters = groupClausesInFilters.reduce(
        (count, clause) => (scdGroupPropertiesWithoutPrefix.has(clause?.value) ? count + 1 : count),
        0,
      );
    }

    const usesFunnelAnyOrder = showBehaviorClauses.some((clause) => clause.behavior?.funnelOrder === FunnelOrder.Any);
    const usesFunnelExcludeEvent = showBehaviorClauses.some((clause) => clause.behavior?.exclusions?.length);
    const usesFunnelHoldProperty = showBehaviorClauses.some((clause) => clause.behavior?.aggregateBy?.length);

    const trackingData: ClientQueryTrackingProps = {
      'report info: title': this.title,
      'report info: behavior breakdowns': behaviorGroupClauses,
      'report info: behavior filters': behaviorFilterClauses,
      'report info: behavior inline filters': behaviorInlineFilters,
      'report info: datasets': datasets,
      'report info: # of datasets': datasets.length,
      [QueryTrackingPropName.ChartType]: this.displayOptions.chartType,
      'report info: style': this.displayOptions.plotStyle,
      'report info: analysis': this.displayOptions.analysis,
      'report info: rolling window size': this.displayOptions.rollingWindowSize,
      'report info: value': this.displayOptions.value,
      'report info: list of Cohort clauses': cohortList.map(
        (clause) => `${clause.negated ? `[Not in] ` : ``}${clause.name}`,
      ),
      'report info: list of Bookmark Show Clauses': this.sections.show.toBookmarkData(),
      'report info: list of Compare clauses': compareClauses,
      'report info: list of Compare operators': showBehaviorClauses.map((c) => c.measurement.math),
      'report info: list of Compare per-user operators': showBehaviorClauses.map(
        (c) => c.measurement.perUserAggregation,
      ),
      'report info: list of Group By clauses': groupClauseValues,
      'report info: list of Custom Property Group By clauses': customPropertyGroupClauses.map((c) =>
        c.customProperty ? c.customProperty.name : c.customPropertyId,
      ),
      'report info: list of Filter clauses': filterClauseValues,
      'report info: list of Inline Filter clauses': inlineFilterClauseValues,
      'report info: # of hidden clause types': hiddenClauseTypes,
      'report info: list of Compare clause types': showBehaviorClauses.map((c) => c.behavior.resourceType),
      'report info: # of Cohort dimensions': groupByCohortsClauses.length,
      'report info: # of Cohort clauses': cohortList.length,
      'report info: # of Negated Cohort clauses': cohortList.filter((clause) => clause.negated).length,
      'report info: # of Inverse Cohort Pairs': numInverseCohortsPairs,
      'report info: # of Compare clauses': compareClauses.length,
      'report info: # of Group By clauses': groupClauseValues.length,
      'report info: # of Filter clauses': filterClauseValues.length,
      'report info: # of inline Filter clauses': inlineFilterClauseValues.length,
      'report info: # of formula show clauses': formulaShowClauses.length,
      'report info: borrowed properties': borrowedPropertiesTrackingData,
      'report info: # of borrowed properties': borrowedPropertiesTrackingData.length,
      'report info: custom properties with nested borrowed properties': nestedBorrowedPropertiesTrackingData,
      'report info: # of custom properties with nested borrowed properties':
        nestedBorrowedPropertiesTrackingData.length,
      '# formula errors': error?.formula_errors?.length || 0,
      ...(error?.formula_errors?.length
        ? {
            'formula error': error.formula_errors[0].message,
            'invalid formula': formulaShowClauses[error.formula_errors[0].index]?.definition,
          }
        : {}),
      'report info: time aggregation unit': timeClause.unit,
      'report info: time window unit': timeClause.window ? timeClause.window.unit : null,
      'report info: time window value': timeClause.window ? timeClause.window.value : null,
      'report info: time from date': Array.isArray(timeClause.value)
        ? moment.tz(timeClause.value[0], `America/Los_Angeles`).format(TIMESTAMP_WITH_TIMEZONE_FORMAT)
        : null,
      'report info: time to date':
        Array.isArray(timeClause.value) && timeClause.value.length > 1
          ? moment.tz(timeClause.value[1], `America/Los_Angeles`).format(TIMESTAMP_WITH_TIMEZONE_FORMAT)
          : null,
      'report info: time uses sub-day granularity': hasSubDayGranularityTrackingData,
      'report info: date range type': timeClause.dateRangeType || null,
      'report info: number of days queried': timeClause.getNumberOfDaysQueried(),
      'report info: uses custom units': this.sections.show.hasCustomUnits(),
      'report info: uses cohorts': !!(cohortList.length || filterClauseValues.includes(`$cohorts`)),
      'report info: uses cohorts over time': this.isCohortsOverTime(),
      [commonTrackingEventPropertyName.numCustomBucketGroupBys]: this.sections.group.clauses.filter(
        (c) => c.customBucket,
      ).length,
      'report info: # of custom events used': showBehaviorClauses.filter((clause) => clause.customEventId).length,
      'report info: # of properties aggregated': aggregationClauses.length,
      'report info: # of custom properties aggregated': aggregationClauses.filter(
        (clause) => clause.measurement.property.customProperty || clause.measurement.property.customPropertyId,
      ).length,
      'report info: list of properties aggregated': aggregationClauses.map((clause) => clause.behavior.name),
      'report info: # custom property filters': customPropertyFilterClauses.length,
      'report info: # custom property group-bys': customPropertyGroupClauses.length,
      'report info: list of Attribute By Behaviors': attributeByBehaviors,
      'report info: list of Attribute By Filters': attributeByFilters,
      'report info: list of SCD options for Filters': scdOptionsForFilters,
      'report info: list of SCD options for Breakdowns': scdOptionsForBreakdowns,
      'report info: list of SCD options for Show Behaviors': scdOptionsForShowBehaviors,
      // TODO(mack): Once multi-touch attribution is GA, Remove the properties below that extract property/dateRange
      // from the behavior object. Instead, create custom properties for them.
      'report info: list of Attribute By Properties': attributeByBehaviors.map((b) => b?.property),
      'report info: list of Attribute By Lookback Windows': attributeByBehaviors.map((b) => b?.dateRange),
      'report info: list of Metric Attributions': multiAttributionMetrics,
      num_group_by_custom_properties: customPropertyGroupClauses.length,
      ...(this.liftComparison || this.timeComparison
        ? {
            [LIFT_TRACKING_PREFIX]: LiftTypeTrackingName[this.liftComparison?.type || TIME_COMPARE_NON_LIFT_KEY],
          }
        : {}),
      'Legend table enabled': true,
      'report info: retention stats': retentionStats,
      'report info: funnel stats': funnelStats,
      'report info: pqa stats': {
        hasPQAFilters: pqaFilters.length !== 0,
        hasPQAGroups: pqaBreakdowns.length !== 0,
        hasPQAFeatures: pqaFilters.length > 0 || pqaBreakdowns.length > 0,
        globalDataGroupId,
        globalDataGroupName,
        globalDataGroupProperty,
      },
      'report info: user-group query': isUserGroupQuery,
      'report info: latest group property usage stats': {
        numOfBreakdownProps: numOfUserGroupPropsInBreakdowns,
        numOfFilterProps: numOfUserGroupPropsInFilters,
        numOfProps: numOfUserGroupPropsInBreakdowns + numOfUserGroupPropsInFilters,
      },
      'report info: uses funnel any order': usesFunnelAnyOrder,
      'report info: uses funnel exclude event': usesFunnelExcludeEvent,
      'report info: uses funnel hold property': usesFunnelHoldProperty,
      'report info: funnel reentry enabled with ff':
        mixpanel.flags?.get_feature_data_sync(`funnel_reentry`, `disabled`) === `enabled`,
    };

    if (id) {
      trackingData[`report id`] = id;
    }

    if (this.sections.show.hasXauClauses()) {
      const {xauOperators} = this._xauTrackingData();
      trackingData[`report info: list of XAU operators`] = xauOperators;
      trackingData[`report info: # of XAU operators`] = xauOperators.length;
    }

    // // formula tracking
    if (metricsResource) {
      const savedFormulas = formulaShowClauses.filter((clause) => clause.id !== undefined);
      Object.assign(trackingData, {
        'report info: # of formula show clauses': formulaShowClauses.length,
        'report info: # of saved formulas': savedFormulas.length,
        'report info: list of saved formulas': savedFormulas.map((formulaShowClause) =>
          getFormulaShowClauseTrackingProps({
            behaviorsResource,
            formulaShowClause,
            i18n,
            metricsResource,
          }),
        ),
      });
    }

    return trackingData;
  }

  get valid() {
    return (
      this.sections instanceof BuilderSections &&
      this.sections.valid &&
      this.displayOptions &&
      [`chartType`, `plotStyle`, `analysis`, `value`].every((v) =>
        Object.prototype.hasOwnProperty.call(this.displayOptions, v),
      )
    );
  }

  getLiftTypeOverrides() {
    return getLiftTypeOverridesForInsightsBookmark(this.toBookmarkData());
  }

  appendAdditionalFilters(additionalFilters: Segmentation[]) {
    const newParams = getBookmarkParamsWithAdditionalFilters(
      this.toBookmarkData(),
      additionalFilters.map((attrs) => new PropertyFilter(attrs)),
    );
    this.sections = BuilderSections.deserialize(newParams.sections as any);
  }

  overrideTimeSection(dateRangeOverride: DateRangeAttributes, timeUnitOverride: TimeUnit) {
    const newParams = getBookmarkParamsWithDateRange(
      this.toBookmarkData(),
      new DateRange(dateRangeOverride),
      timeUnitOverride,
    );
    this.sections = BuilderSections.deserialize(newParams.sections as any);
  }

  overrideChartType(chartTypeOverride: ChartType) {
    const newParams = getBookmarkParamsWithChartType(this.toBookmarkData(), chartTypeOverride);
    this.displayOptions = newParams.displayOptions;
  }

  overrideReportSections(reportSectionsOverride: InsightsReportSectionsOverride) {
    this.sections = BuilderSections.deserialize({
      ...this.sections.serialize(),
      ...(reportSectionsOverride as any),
    });
  }

  applyErfTreatments(erfTreatments: CohortSegment[]) {
    this.sections.show.clauses = this.sections.show.clauses.flatMap((clause): ShowClauseClassType[] => {
      if (clause instanceof FormulaShowClause) {
        return [clause];
      }
      if (clause.behavior.filters?.length > 1 && clause.behavior.filtersDeterminer === FilterSection.DETERMINER_ANY) {
        // We can't add cohort as a filter if there's already multiple filters that are OR-ed. This should be rare.
        return [clause];
      }

      if (clause instanceof BehaviorShowClause && clause.behavior.type === MetricType.Funnel) {
        if (this.sections.show.clauses.length === 1) {
          this.sections.group = this.sections.group.appendClause(
            new GroupClause({
              cohorts: erfTreatments.map((erfTreatment) => {
                return erfTreatment.cohort;
              }),
            }),
          );
        }

        return [clause];
      }

      return erfTreatments.map((cohort) => {
        const cohortFilterClause = FilterClause.defaultCohortsClause().extend({
          filterValue: [cohort],
        });
        return new BehaviorShowClause({
          behavior: {
            ...clause.behavior,
            filters: [...(clause.behavior.filters || []), cohortFilterClause],
            filtersDeterminer: FILTER_DETERMINER_ALL,
          },
          measurement: {...clause.measurement},
        });
      });
    });
  }

  isFilterDeterminerAll() {
    return this.sections.filter.determiner === FilterSection.DETERMINER_ALL;
  }

  hasTopEvents() {
    return this.sections.show.getBehaviorClauses().some((clause) => clause.behavior.name === SPECIAL_EVENTS.TOP_EVENTS);
  }

  isEmpty() {
    return !this.sections?.show?.clauses?.length;
  }
}
